import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  extendControlRender,
  extendsRender,
  headers,
  POLLING_INTERVAL,
  renderAction,
  renderCategory,
  renderLimit,
  renderStatus,
} from './constants';
import { Maybe } from '@apolloGenerated';
import { bem } from 'classnames-bem';
import { isArray } from '@apollo/client/utilities';
import { DateTime } from 'luxon';
import sdk from '@aml/sdk';
import { StatusField } from '@aml/sdk/dashboard';
import { HeaderControls } from './HeaderControls';
import {
  SortType,
  TableController,
  Spacer,
  LoaderOverlay,
  Informer,
} from '@rubin-dev/goblin';
import styles from './transactions.module.scss';
import { renderCellDate, renderCellHash } from '@shared/ui';
import { AppContext } from '../../contexts';
import { DashboardFilters } from './TransactionsScreen';
import { DashboardOrder } from './types';
import { useTranslation } from 'react-i18next';

type OverLimitProps = {
  updateTotal: () => void;
  initFilters: DashboardFilters;
};
const pageSize = 10;
export const OverLimitTable: FC<PropsWithChildren<OverLimitProps>> = ({
  children,
  updateTotal,
  initFilters: { hash: initHash, dateFrom: initDateFrom, dateTo: initDateTo },
}) => {
  const { t } = useTranslation();
  const { language } = useContext(AppContext);
  const [page, setPage] = useState(1);

  const [orderBy, setSortBy] = useState<DashboardOrder | null>(DashboardOrder.Asc);

  const [date, setDate] = useState<DateTime[]>(
    initDateFrom && initDateTo
      ? [DateTime.fromISO(initDateFrom), DateTime.fromISO(initDateTo)]
      : [],
  );
  const [hash, setHash] = useState<string>(initHash || '');
  const [loading, setLoading] = useState<boolean>(false);

  const [extendRow, setExtendRow] = useState<Maybe<number>>(null);
  const [params, setParams] = useState<{
    hash?: string;
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
    status?: 'CLEAN' | 'BLOCKED' | 'OVER_LIMIT';
    orderBy?: DashboardOrder;
  }>({});

  const { data, isLoading, refetch } = sdk.dashboard.queries.useGetTransactionList(
    params.hash,
    params.page,
    params.pageSize,
    params.from,
    params.to,
    params.status,
    params.orderBy,
    {
      refetchInterval: POLLING_INTERVAL,
    },
  );

  const { mutateAsync, status } =
    sdk.dashboard.mutations.usePostSetStatusToRiskTransaction();

  useEffect(() => {
    setParams({
      hash,
      page,
      pageSize,
      from: isArray(date) && date?.length > 1 ? date[0].toISODate() : undefined,
      to: isArray(date) && date?.length > 1 ? date[1].toISODate() : undefined,
      status: 'OVER_LIMIT',
      orderBy: orderBy || undefined,
    });
  }, [data, date, page, pageSize, orderBy, status]);

  const changeStatus = async (id: number, status: StatusField) => {
    setLoading(true);

    await mutateAsync({
      id,
      status,
    });

    await refetch();

    if (updateTotal) updateTotal();
    setLoading(false);
  };

  const edge = useMemo(
    () =>
      data?.transactionList?.map((item) => ({
        hash: renderCellHash({
          hash: item.tx_id || '',
          network: item.network,
          type: 'transaction',
          once: true,
        }),
        date: renderCellDate({ timestamp: item?.updated_at || 0 }),
        token: item.network.toUpperCase(),
        category: renderCategory(item.riskCategories),
        receivingLimit: renderLimit(item.riskCategories),
        status: renderStatus(item.status),
        actions: renderAction(changeStatus, item.id),
        extendsRender: extendRow === item.id ? extendsRender(item) : '',
        extendControl: extendControlRender(extendRow === item.id, () =>
          extendRow ? setExtendRow(null) : setExtendRow(item.id),
        ),
      })) || [],
    [data?.transactionList, extendRow, language],
  );
  const total = data?.total || 0;

  const onSubmit = async () => {
    let from = undefined;
    let to = undefined;

    if (isArray(date)) {
      if (date?.length === 1) {
        setDate([date[0], date[0]]);
        from = date[0].toISODate();
        to = date[0].toISODate();
      }

      if (date?.length > 1) {
        setDate([date[0], date[1]]);
        from = date[0].toISODate();
        to = date[1].toISODate();
      }
    }

    setParams({
      hash,
      page,
      pageSize,
      from,
      to,
      status: 'OVER_LIMIT',
      orderBy: orderBy || undefined,
    });
  };

  return (
    <>
      <div className={bem('controls')}>
        <HeaderControls
          date={date}
          hash={hash}
          onChangeHash={setHash}
          onChangeDate={setDate}
          onConfirm={onSubmit}
        />
        {children}
      </div>
      <div className={styles.tableWrapper}>
        <LoaderOverlay show={isLoading || loading} />
        <Spacer size={4} />
        {!edge.length ? (
          <div className={styles.information}>
            <Informer
              type="empty"
              title={t('strings.noresults')}
              subtitle={t('explorer.noResultFound')}
            />
          </div>
        ) : (
          <TableController
            data={edge}
            headers={headers()}
            total={total}
            initPage={page}
            initSort={orderBy ? (orderBy.toLowerCase() as SortType) : null}
            initSortBy={'date'}
            onSort={(sort) =>
              setSortBy(
                sort === 'asc'
                  ? DashboardOrder.Asc
                  : sort === 'desc'
                  ? DashboardOrder.Desc
                  : null,
              )
            }
            onChangePage={setPage}
            pageSize={pageSize}
            notResetPage
          />
        )}
      </div>
    </>
  );
};
